import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, ReferenceLine } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isAdmin } from '../utils/roles';
import { FiFileText, FiDownload, FiCalendar, FiFilter, FiTrendingUp, FiHelpCircle } from 'react-icons/fi';

function formatDateAU(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const fmtH = v => { const s = Math.round(parseFloat(v) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };

export default function Reports({ embedded }) {
  const { user, authFetch } = useAuth();
  const { isDark } = useTheme();
  const tooltipStyle = { background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: isDark ? '#f1f5f9' : '#1e293b', boxShadow: 'var(--shadow)' };
  const [reportType, setReportType] = useState('work-orders');
  const [filters, setFilters] = useState({
    status: '', type: '', assigned_to: '', asset_id: '',
    date_from: '', date_to: '',
    status_id: '', user_id: '',
  });
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsView, setMetricsView] = useState('weekly');
  const [showTips, setShowTips] = useState(false);
  const [adherenceTarget, setAdherenceTarget] = useState(80);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  useEffect(() => {
    authFetch('/api/users').then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
    authFetch('/api/assets?limit=200').then(r => r.ok ? r.json() : {}).then(res => setAssets(res && res.data ? res.data : (Array.isArray(res) ? res : []))).catch(() => {});
    authFetch('/api/assets/statuses').then(r => r.ok ? r.json() : []).then(setStatuses).catch(() => {});
  }, []);

  useEffect(() => {
    authFetch('/api/admin/site-settings').then(r => r.ok ? r.json() : {}).then(d => {
      if (d.adherence_target !== undefined) setAdherenceTarget(d.adherence_target);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedPeriod(null);
  }, [metricsView]);

  useEffect(() => {
    if (reportType === 'metrics' && isAdmin(user)) {
      setMetricsLoading(true);
      let url;
      if (metricsView === 'weekly') url = '/api/admin/metrics/weekly?weeks=8';
      else if (metricsView === 'monthly') url = '/api/admin/metrics/monthly?months=12';
      else url = '/api/admin/metrics/yearly?years=5';
      authFetch(url)
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          const normalized = data.reverse().map((d, i) => ({
            ...d,
            period_start: d.period_start || d.week_start,
            period_end: d.period_end || d.week_end,
            period_label: d.period_label || null,
            week_num: i + 1,
          }));
          setMetrics(normalized);
          setMetricsLoading(false);
        })
        .catch(() => setMetricsLoading(false));
    }
  }, [reportType, metricsView]);

  const handleDownload = async (format) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ format });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });

      const res = await authFetch(`/api/reports/${reportType}?${params.toString()}`);
      if (!res.ok) throw new Error('Download failed');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
    setDownloading(false);
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const params = new URLSearchParams({ format: 'json' });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await authFetch(`/api/reports/${reportType}?${params.toString()}`);
      if (res.ok) { const data = await res.json(); setPreviewData(data); }
    } catch (err) { console.error('Preview error:', err); }
    setPreviewLoading(false);
  };

  const previewColumns = {
    'work-orders': [
      { key: 'wo_number', label: 'WO #', width: 90 },
      { key: 'name', label: 'Name', width: 200 },
      { key: 'type', label: 'Type', width: 60 },
      { key: 'status', label: 'Status', width: 80 },
      { key: 'priority', label: 'Priority', width: 70 },
      { key: 'assigned_to_name', label: 'Assigned To', width: 120 },
      { key: 'due_date', label: 'Due Date', width: 90, fmt: formatDateAU },
      { key: 'asset_name', label: 'Asset', width: 120 },
    ],
    'assets': [
      { key: 'tag', label: 'Asset ID', width: 100 },
      { key: 'name', label: 'Name', width: 200 },
      { key: 'serial_number', label: 'Serial #', width: 120 },
      { key: 'status_name', label: 'Status', width: 100 },
    ],
    'time-summary': [
      { key: 'user_name', label: 'Employee', width: 140 },
      { key: 'wo_number', label: 'WO #', width: 90 },
      { key: 'wo_name', label: 'Work Order', width: 180 },
      { key: 'operation_number', label: 'Op #', width: 60 },
      { key: 'total_hours', label: 'Hours', width: 70 },
      { key: 'entry_count', label: 'Entries', width: 60 },
      { key: 'asset_name', label: 'Asset', width: 120 },
    ],
  };

  const getXLabel = (d) => {
    if (!d) return '';
    if (metricsView === 'yearly') return d.period_label || d.period_start;
    if (metricsView === 'weekly') return `Week ${d.week_num}`;
    return formatDateShort(d.period_start);
  };

  const getTooltipLabel = (d) => {
    if (!d) return '';
    if (d.period_label) return d.period_label;
    const range = `${formatDateAU(d.period_start)} - ${formatDateAU(d.period_end)}`;
    if (metricsView === 'weekly') return `Week ${d.week_num}: ${range}`;
    return range;
  };

  // Current week info
  const getCurrentWeekInfo = () => {
    if (metricsView !== 'weekly' || metrics.length === 0) return null;
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const current = metrics.find(m => m.period_start <= todayStr && m.period_end >= todayStr);
    if (!current) return { week: metrics.length, start: metrics[metrics.length - 1].period_start, end: metrics[metrics.length - 1].period_end };
    return { week: current.week_num, start: current.period_start, end: current.period_end };
  };

  const reportTypes = [
    { key: 'work-orders', label: 'Work Orders', desc: 'Export work order list with status, priority, and assignments' },
    { key: 'assets', label: 'Asset Inventory', desc: 'Export asset catalog with tag, name, serial and status' },
    { key: 'time-summary', label: 'Time Summary', desc: 'Time confirmation totals grouped by employee, work order, and operation' },
  ];

  const viewModes = [
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'yearly', label: 'Yearly' },
  ];

  const tips = [
    { term: 'Adherence', desc: 'Percentage of work orders completed on time within the period. Above 80% is good, below 50% needs attention.' },
    { term: 'Completed', desc: 'Number of work orders marked as Complete within the period.' },
    { term: 'Not Completed', desc: 'Work orders that were due but not completed within the period.' },
    { term: 'Total Due', desc: 'Total number of work orders that had a due date within the period.' },
    { term: 'Hours Logged', desc: 'Total time confirmed by all team members during the period.' },
    { term: 'PM01 / PM02 / PM03', desc: 'Breakdown by work order type. Shows completed vs total (e.g. 3/5 means 3 done out of 5 due).' },
  ];

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Reports</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>Generate PDF and Excel reports</p>
        </div>
      )}

      {/* Report Type Selector */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
        {reportTypes.map(rt => (
          <motion.div key={rt.key}
            whileHover={{ scale: 1.01 }}
            onClick={() => setReportType(rt.key)}
            style={{
              padding: 16, borderRadius: 12, cursor: 'pointer',
              background: reportType === rt.key ? 'rgba(37,99,235,0.05)' : 'var(--bg-card)',
              border: `2px solid ${reportType === rt.key ? '#2563eb' : 'var(--border)'}`,
              transition: 'all 0.2s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <FiFileText size={16} color={reportType === rt.key ? '#2563eb' : 'var(--text-secondary)'} />
              <span style={{ fontSize: 15, fontWeight: 600, color: reportType === rt.key ? '#2563eb' : 'var(--text-primary)' }}>{rt.label}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{rt.desc}</div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      {reportType !== 'metrics' && <div style={{
        background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <FiFilter size={16} color="var(--text-secondary)" />
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Filters</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {/* Date range (all report types) */}
          <div>
            <label style={labelStyle}>Date From</label>
            <div style={{ position: 'relative' }}>
              <FiCalendar size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="date" value={filters.date_from}
                onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                style={{ ...inputStyle, paddingLeft: 32 }}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Date To</label>
            <div style={{ position: 'relative' }}>
              <FiCalendar size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="date" value={filters.date_to}
                onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                style={{ ...inputStyle, paddingLeft: 32 }}
              />
            </div>
          </div>

          {/* Work order filters */}
          {reportType === 'work-orders' && (
            <>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                  <option value="">All Statuses</option>
                  {['New', 'In Progress', 'Complete', 'TECO'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                  <option value="">All Types</option>
                  {['PM01', 'PM02', 'PM03'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Assigned To</label>
                <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} style={inputStyle}>
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </>
          )}

          {/* Asset filters */}
          {reportType === 'assets' && (
            <>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={filters.status_id} onChange={e => setFilters(f => ({ ...f, status_id: e.target.value }))} style={inputStyle}>
                  <option value="">All Statuses</option>
                  {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </>
          )}

          {/* Time summary filters */}
          {reportType === 'time-summary' && (
            <>
              <div>
                <label style={labelStyle}>Employee</label>
                <select value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
                  <option value="">All Employees</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Asset</label>
                <select value={filters.asset_id} onChange={e => setFilters(f => ({ ...f, asset_id: e.target.value }))} style={inputStyle}>
                  <option value="">All Assets</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.tag} - {a.name}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>}

      {/* Download Buttons */}
      {reportType !== 'metrics' && (<div style={{
        background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center'
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginRight: 8 }}>Download as:</span>
        <motion.button
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => handleDownload('pdf')}
          disabled={downloading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px',
            borderRadius: 10, border: 'none', background: '#dc2626', color: '#fff',
            cursor: downloading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600,
            opacity: downloading ? 0.7 : 1, boxShadow: '0 2px 8px rgba(220,38,38,0.3)'
          }}
        >
          <FiDownload size={16} /> PDF
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => handleDownload('xlsx')}
          disabled={downloading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px',
            borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff',
            cursor: downloading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600,
            opacity: downloading ? 0.7 : 1, boxShadow: '0 2px 8px rgba(22,163,74,0.3)'
          }}
        >
          <FiDownload size={16} /> Excel
        </motion.button>
        <div style={{ flex: 1 }} />
        <motion.button
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={handlePreview}
          disabled={previewLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px',
            borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            cursor: previewLoading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600,
            opacity: previewLoading ? 0.7 : 1, boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
          }}
        >
          <FiFileText size={16} /> {previewLoading ? 'Loading...' : 'Generate List'}
        </motion.button>
      </div>)}

      {/* Data Preview Table */}
      {previewData && reportType !== 'metrics' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginTop: 16, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              {previewData.total} {reportType === 'work-orders' ? 'work orders' : reportType === 'assets' ? 'assets' : 'entries'}
            </span>
            <button onClick={() => setPreviewData(null)}
              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}>
              Close
            </button>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 500 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-input)', position: 'sticky', top: 0, zIndex: 1 }}>
                  {(previewColumns[reportType] || []).map(col => (
                    <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.data.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {(previewColumns[reportType] || []).map(col => (
                      <td key={col.key} style={{ padding: '8px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap', maxWidth: col.width, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
                {previewData.data.length === 0 && (
                  <tr><td colSpan={99} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No data matching the selected filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Metrics Section */}
      {reportType === 'metrics' && isAdmin(user) && (
        <div>
          {/* View Mode Selector + Tips Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
              {viewModes.map(vm => (
                <button key={vm.key} onClick={() => setMetricsView(vm.key)}
                  style={{
                    padding: '6px 16px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: metricsView === vm.key ? '#2563eb' : 'transparent',
                    color: metricsView === vm.key ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.2s'
                  }}
                >{vm.label}</button>
              ))}
            </div>
            <button onClick={() => setShowTips(t => !t)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8,
                border: '1px solid var(--border)', background: showTips ? 'rgba(37,99,235,0.05)' : 'var(--bg-card)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                color: showTips ? '#2563eb' : 'var(--text-secondary)'
              }}
            ><FiHelpCircle size={14} /> {showTips ? 'Hide Tips' : 'Show Tips'}</button>
          </div>

          {/* Tips Panel */}
          {showTips && (
            <div style={{
              background: 'rgba(37,99,235,0.04)', borderRadius: 12, padding: 16, border: '1px solid rgba(37,99,235,0.15)',
              marginBottom: 20
            }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiHelpCircle size={14} /> Understanding Your Metrics
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                {tips.map(tip => (
                  <div key={tip.term} style={{ fontSize: 13, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{tip.term}:</span>{' '}
                    <span style={{ color: 'var(--text-secondary)' }}>{tip.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metricsLoading ? (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[...Array(4)].map((_, i) => (
                  <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                    <div className="skeleton" style={{ width: '60%', height: 14, marginBottom: 8 }} />
                    <div className="skeleton" style={{ width: '40%', height: 24 }} />
                  </div>
                ))}
              </div>
              <div className="skeleton" style={{ height: 200, borderRadius: 10 }} />
            </div>
          ) : metrics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No data available</div>
          ) : (
            <>
              {/* Summary cards for latest period */}
              {metrics.length > 0 && (() => {
                const latest = metrics[metrics.length - 1];
                const adherenceColor = latest.adherence_pct >= 80 ? '#16a34a' : latest.adherence_pct >= 50 ? '#f59e0b' : '#dc2626';
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
                    <MetricCard label="Adherence" value={`${latest.adherence_pct}%`} color={adherenceColor}
                      sub={`${formatDateAU(latest.period_start)} - ${formatDateAU(latest.period_end)}`} />
                    <MetricCard label="Completed" value={latest.completed} color="#16a34a" />
                    <MetricCard label="Not Completed" value={latest.not_completed} color="#dc2626" />
                    <MetricCard label="Total Due" value={latest.total_due} color="#0ea5e9" />
                    <MetricCard label="Hours Logged" value={fmtH(latest.hours_logged)} color="#8b5cf6" />
                  </div>
                );
              })()}

              {/* Current Week Indicator */}
              {metricsView === 'weekly' && (() => {
                const cw = getCurrentWeekInfo();
                return cw ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 16px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)' }}>
                    <FiCalendar size={16} color="#2563eb" />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>Current: Week {cw.week}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDateAU(cw.start)} - {formatDateAU(cw.end)}</span>
                  </div>
                ) : null;
              })()}

              {/* Adherence Trend */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiTrendingUp size={16} /> {metricsView === 'weekly' ? 'Weekly' : metricsView === 'monthly' ? 'Monthly' : 'Yearly'} Adherence Trend
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={metrics}>
                    <XAxis dataKey="period_start" tick={{ fontSize: 11 }} tickFormatter={d => {
                      const m = metrics.find(x => x.period_start === d);
                      if (m) return getXLabel(m);
                      return formatDateShort(d);
                    }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                    <Tooltip
                      labelFormatter={d => {
                        const m = metrics.find(x => x.period_start === d);
                        return m ? getTooltipLabel(m) : d;
                      }}
                      formatter={(v, name) => [name === 'adherence_pct' ? `${v}%` : v, name === 'adherence_pct' ? 'Adherence' : name]}
                      contentStyle={tooltipStyle}
                    />
                    <ReferenceLine y={adherenceTarget} stroke="#2563eb" strokeDasharray="6 3" strokeWidth={2}
                      label={{ value: `Target ${adherenceTarget}%`, position: 'right', fill: '#2563eb', fontSize: 11, fontWeight: 600 }} />
                    <Bar dataKey="adherence_pct" name="Adherence" radius={[4, 4, 0, 0]} cursor="pointer"
                      onClick={(data) => setSelectedPeriod(prev => prev?.period_start === data.period_start ? null : data)}>
                      {metrics.map((entry, i) => (
                        <Cell key={i} fill={entry.adherence_pct >= 80 ? '#16a34a' : entry.adherence_pct >= 50 ? '#f59e0b' : '#dc2626'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px 0', fontSize: 10, color: 'var(--text-muted)' }}>
                  {metrics.length > 0 && <span>{formatDateAU(metrics[0].period_start)}</span>}
                  {metrics.length > 1 && <span>{formatDateAU(metrics[metrics.length - 1].period_end)}</span>}
                </div>
              </div>

              {/* Selected Period Detail */}
              {selectedPeriod && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '2px solid #2563eb', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#2563eb' }}>
                      {selectedPeriod.period_label || `${formatDateAU(selectedPeriod.period_start)} - ${formatDateAU(selectedPeriod.period_end)}`}
                    </h3>
                    <button onClick={() => setSelectedPeriod(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)', lineHeight: 1 }}>x</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                    <div style={{ textAlign: 'center', padding: 10, borderRadius: 8, background: 'rgba(37,99,235,0.04)' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: selectedPeriod.adherence_pct >= 80 ? '#16a34a' : selectedPeriod.adherence_pct >= 50 ? '#f59e0b' : '#dc2626' }}>{selectedPeriod.adherence_pct}%</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Adherence</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 10, borderRadius: 8, background: 'rgba(22,163,74,0.04)' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>{selectedPeriod.completed}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Completed</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 10, borderRadius: 8, background: 'rgba(220,38,38,0.04)' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626' }}>{selectedPeriod.not_completed}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Not Completed</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 10, borderRadius: 8, background: 'rgba(14,165,233,0.04)' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#0ea5e9' }}>{selectedPeriod.total_due}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Total Due</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 10, borderRadius: 8, background: 'rgba(139,92,246,0.04)' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#8b5cf6' }}>{fmtH(selectedPeriod.hours_logged)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Hours Logged</div>
                    </div>
                  </div>
                  {selectedPeriod.by_type && Object.keys(selectedPeriod.by_type).length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {Object.entries(selectedPeriod.by_type).map(([type, data]) => (
                        <div key={type} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          <span style={{ fontWeight: 700 }}>{type}:</span> {data.done}/{data.total}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Completed vs Not Completed */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Completed vs Not Completed</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={metrics}>
                    <XAxis dataKey="period_start" tick={{ fontSize: 11 }} tickFormatter={d => {
                      const m = metrics.find(x => x.period_start === d);
                      if (m) return getXLabel(m);
                      return formatDateShort(d);
                    }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={d => {
                        const m = metrics.find(x => x.period_start === d);
                        return m ? getTooltipLabel(m) : d;
                      }}
                      contentStyle={tooltipStyle}
                    />
                    <Bar dataKey="completed" name="Completed" fill="#16a34a" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="not_completed" name="Not Completed" fill="#dc2626" stackId="a" radius={[4, 4, 0, 0]} />
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px 0', fontSize: 10, color: 'var(--text-muted)' }}>
                  {metrics.length > 0 && <span>{formatDateAU(metrics[0].period_start)}</span>}
                  {metrics.length > 1 && <span>{formatDateAU(metrics[metrics.length - 1].period_end)}</span>}
                </div>
              </div>

              {/* Hours Logged */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Hours Logged Per {metricsView === 'weekly' ? 'Week' : metricsView === 'monthly' ? 'Month' : 'Year'}</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={metrics}>
                    <XAxis dataKey="period_start" tick={{ fontSize: 11 }} tickFormatter={d => {
                      const m = metrics.find(x => x.period_start === d);
                      if (m) return getXLabel(m);
                      return formatDateShort(d);
                    }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={d => {
                        const m = metrics.find(x => x.period_start === d);
                        return m ? getTooltipLabel(m) : d;
                      }}
                      formatter={(v) => [`${v}h`, 'Hours']}
                      contentStyle={tooltipStyle}
                    />
                    <Bar dataKey="hours_logged" name="Hours" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px 0', fontSize: 10, color: 'var(--text-muted)' }}>
                  {metrics.length > 0 && <span>{formatDateAU(metrics[0].period_start)}</span>}
                  {metrics.length > 1 && <span>{formatDateAU(metrics[metrics.length - 1].period_end)}</span>}
                </div>
              </div>

              {/* Breakdown Table */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', overflow: 'auto' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {metricsView === 'weekly' ? 'Weekly' : metricsView === 'monthly' ? 'Monthly' : 'Yearly'} Breakdown
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      {['Period', 'Due', 'Complete', 'Not Complete', 'Adherence', 'Hours', 'PM01', 'PM02', 'PM03'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.map((w, i) => {
                      const adherenceColor = w.adherence_pct >= 80 ? '#16a34a' : w.adherence_pct >= 50 ? '#f59e0b' : '#dc2626';
                      const periodDisplay = w.period_label
                        ? w.period_label
                        : `${formatDateAU(w.period_start)} - ${formatDateAU(w.period_end)}`;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{periodDisplay}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-primary)' }}>{w.total_due}</td>
                          <td style={{ padding: '8px 10px', color: '#16a34a', fontWeight: 600 }}>{w.completed}</td>
                          <td style={{ padding: '8px 10px', color: '#dc2626', fontWeight: 600 }}>{w.not_completed}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: adherenceColor }}>{w.adherence_pct}%</span>
                          </td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-primary)' }}>{fmtH(w.hours_logged)}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{w.by_type?.PM01 ? `${w.by_type.PM01.done}/${w.by_type.PM01.total}` : '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{w.by_type?.PM02 ? `${w.by_type.PM02.done}/${w.by_type.PM02.total}` : '—'}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{w.by_type?.PM03 ? `${w.by_type.PM03.done}/${w.by_type.PM03.total}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color, sub }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'var(--bg-card)', borderRadius: 10, padding: 16,
        border: '1px solid var(--border)', textAlign: 'center'
      }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </motion.div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--bg-card)',
  fontFamily: 'inherit'
};
